/**
 * Zod validation schemas for Collaborator types
 */

import { z } from 'zod';

export const addressSchema = z.object({
  id: z.string(),
  type: z.string(),
  house: z.string().optional(),
  street: z.string().optional(),
  landmark: z.string().optional(),
  vtc: z.string().optional(),
  subdistrict: z.string().optional(),
  district: z.string().optional(),
  state: z.string().optional(),
  post_office: z.string().optional(),
  pincode: z.string().optional(),
  country: z.string().optional(),
  full_address: z.string().optional(),
});

export const collaboratorSchema = z.object({
  id: z.string(),
  company_name: z.string().min(1, 'Company name is required'),
  contact_person: z.string().min(1, 'Contact person is required'),
  contact_number: z.string().regex(/^\d{10}$/, 'Invalid phone number'),
  email: z.preprocess(
    (val) => (val === '' || val === null ? undefined : val),
    z.string().email('Invalid email format').optional()
  ),
  gst_number: z
    .string()
    .min(1, 'GST number is required')
    .transform((val) => val.toUpperCase().trim())
    .refine(
      (val) => /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(val),
      'Invalid GST number format. GST number must be 15 characters (e.g., 27AAAAA0000A1Z5)'
    ),
  pan_number: z.string().optional(),
  bank_name: z.string().optional(),
  bank_account_no: z.string().optional(),
  bank_ifsc: z
    .string()
    .optional()
    .refine(
      (val) => !val || val === '' || /^[A-Z]{4}0[A-Z0-9]{6}$/.test(val),
      'Invalid IFSC code'
    ),
  experience: z.string().optional(),
  logo: z.string().optional(),
  address: addressSchema.optional(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const createCollaboratorSchema = collaboratorSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
  is_active: true,
});

export const updateCollaboratorSchema = createCollaboratorSchema.partial();

export type Collaborator = z.infer<typeof collaboratorSchema>;
export type CreateCollaborator = z.infer<typeof createCollaboratorSchema>;
export type UpdateCollaborator = z.infer<typeof updateCollaboratorSchema>;
